import React from 'react';

/**
 * Default boolean widget. Renders identically to text/select fields:
 *   - Uppercase label above (rendered here, since RJSF sets displayLabel=false for booleans)
 *   - A bordered row matching input height, with a styled checkbox + "Sí/No" indicator.
 */
function CheckboxWidget(props) {
  const { id, value, disabled, readonly, required, onChange, autofocus, label, schema } = props;

  const isChecked = !!value;
  const isDisabled = disabled || readonly;
  const title = label || schema?.title;

  function handleChange(e) {
    onChange(e.target.checked);
  }

  // When readonly/disabled the field must follow the neutral readonly palette
  // shared by the other locked fields (zinc-100 bg / zinc-500 text — the
  // `--readonly-bg` / `--readonly-text` defaults in tailwind.css), NOT the blue
  // "active" palette used for an editable checked checkbox. The readonly CSS
  // rule only greys native inputs, so this wrapper row must opt in explicitly.
  const rowClass = isDisabled
    ? 'bg-zinc-100 dark:bg-white/[0.06] cursor-not-allowed pointer-events-none'
    : isChecked
    ? 'bg-blue-50 border-blue-300 cursor-pointer'
    : 'bg-white dark:bg-zinc-950/50 theme-border hover:border-blue-300 hover:bg-blue-50/40 cursor-pointer';

  // Sibling text/select fields border off the form-field token (`--ff-borderColor`),
  // while `theme-border` only reads `--border-color` — a different shade. Match the
  // neighbours in the readonly state by bordering off the same token chain.
  const rowStyle = isDisabled ? { borderColor: 'var(--ff-borderColor, var(--border-color))' } : undefined;

  const textClass = isDisabled ? 'text-zinc-500 dark:text-zinc-400' : isChecked ? 'text-blue-700' : 'theme-text-secondary';

  const inputClass = isDisabled
    ? 'w-4 h-4 m-0 rounded border-zinc-300 dark:border-zinc-600 accent-zinc-500 cursor-not-allowed'
    : 'w-4 h-4 m-0 rounded border-zinc-300 dark:border-zinc-600 text-blue-600 focus:ring-2 focus:ring-blue-500/40 cursor-pointer accent-blue-600';

  return (
    <div className="space-y-1">
      {title ? (
        <label htmlFor={id} className="control-label">
          {title}
          {required && <span className="text-danger ml-0.5 normal-case">*</span>}
        </label>
      ) : null}
      <div
        className={`flex items-center gap-2.5 w-full px-4 py-2.5 rounded border transition-all ${rowClass}`}
        style={rowStyle}
        onClick={() => !isDisabled && onChange(!isChecked)}
      >
        <input
          id={id}
          type="checkbox"
          checked={isChecked}
          disabled={isDisabled}
          required={required}
          autoFocus={autofocus}
          onChange={handleChange}
          onClick={e => e.stopPropagation()}
          className={inputClass}
        />
        <span className={`select-none ${textClass}`}>{isChecked ? 'Sí' : 'No'}</span>
      </div>
    </div>
  );
}

export default CheckboxWidget;
